import { useState, useMemo, Fragment } from "react";
import { DatesProvider } from "dates/DatesContext";
import { useDates } from "dates/DatesContext";
import { useModuleEnabled } from "redux/selectors/modules";
import { useLocation, useParams, Redirect } from "react-router-dom";
import { Col, Nav, Row, Table as ReadOnlyTable } from "react-bootstrap";
import useDSPCampaignData from "./useDSPCampaignData";
import useDSPCreativeData from "./useDSPCreativeData";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DASHBOARD_LINKS } from "utils/dashboardLinks";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { buildDSPMetric } from "./metrics";
import useDSPLineItemData from "./useDSPLineItemData";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import moment from "moment";
import DetailedMetric from "components/core/blocks/DetailedMetric";
import { formatCurrency } from "utils/formatNumber";
import Loading from "components/core/blocks/Loading";
import DSPCreativesTable from "./DSPCreativesTable";
import DSPLineItemsTable from "./DSPLineItemsTable";
import { useAppSelector } from "redux/hooks";
import { GOAL_DEFINITIONS, KPI_DEFINITIONS } from "./constants";
import FlightsTable from "./FlightsTable";
import DSPOverlapDashboard from "./DSPOverlapDashboad";
import DSPOrderForm from "./forms/DSPOrderForm";
import { Button } from "components/core/basic/Button";
import DSPLineItemForm from "./forms/DSPLineItemForm";
import CenteredModal from "components/core/basic/CenteredModal";
import { useQueryClient } from "@tanstack/react-query";
import DSPGeoDashboard from "./DSPGeoDashboard";
import { useDSPOrderConversionTrackingProducts } from "./hooks/useDSPOrderConversionTrackingProducts";
import useDSPSellerInfo from "./hooks/useDSPSellerInfo";

const DSP_TABS = [
  { key: "orderSettings", label: "Order Settings" },
  { key: "configuration", label: "Configuration" },
  { key: "lineItems", label: "Line Items" },
  { key: "creatives", label: "Creatives" },
  { key: "audienceOverlap", label: "Audience Overlap" },
  { key: "geo", label: "Geography" },
];

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState(DSP_TABS[0].key);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [showLineItemCreationModal, setShowLineItemCreationModal] = useState(false);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { dspAdvertiser } = useDSPSellerInfo();
  const channel = useAppSelector((state) => state.channel);
  const hasDSPModule = useModuleEnabled("DSP");
  const { id: dspOrderId } = useParams();
  const { group, start, end } = useDates();
  const queryClient = useQueryClient();

  const {
    data: dspCampaignData,
    metadata: dspCampaignMetadata,
    isLoading: isDSPCampaignDataLoading,
  } = useDSPCampaignData("campaign_id", true, true, { campaign_id: dspOrderId });

  const { data: conversionTrackingProducts, isLoading: isConversionTrackingProductsDataLoading } =
    useDSPOrderConversionTrackingProducts(dspOrderId);

  const orderName = dspCampaignMetadata?.[0]?.name;
  const advertiserId = dspCampaignMetadata?.[0]?.advertiser_id;

  const { data: dspTimeseriesData } = useDSPCampaignData(`org_id,${group}`, true, true, { campaign_id: dspOrderId });
  const { metadata: dspMetadata } = useDSPCampaignData("org_id", true, false, {});
  const {
    data: lineItemData,
    metadata: lineItemMetadata,
    isLoading,
  } = useDSPLineItemData("line_item_id", true, true, { order_id: dspOrderId });

  const {
    data: dspCreativeStatsData,
    metadata: dspCreativeMetadata,
    isLoading: isDSPCreativeDataLoading,
  } = useDSPCreativeData("creative_id", true, true, { order_id: dspOrderId });

  const orderOverviewData = useMemo(() => {
    return {
      total: dspCampaignData?.[0] ?? {},
      date:
        dspTimeseriesData?.map((row) => {
          return { ...row, start_date: row.date };
        }) ?? [],
      metadata: dspCampaignMetadata ?? [],
      isLoading: isDSPCampaignDataLoading,
    };
  }, [dspCampaignData, dspTimeseriesData, dspCampaignMetadata, isDSPCampaignDataLoading]);

  const orderDates = useMemo(() => {
    return {
      startDate: moment
        .min(orderOverviewData?.metadata?.[0]?.flights?.map((f) => moment(f.startDateTime)))
        ?.toISOString(),
      endDate: moment.max(orderOverviewData?.metadata?.[0]?.flights?.map((f) => moment(f.endDateTime)))?.toISOString(),
    };
  }, [orderOverviewData?.metadata]);

  const secondaryMetricTileData = useMemo(() => {
    if (!orderOverviewData.metadata) {
      return {};
    }

    const metadata = orderOverviewData?.metadata?.[0] ?? {};
    const processedData = {
      state: snakeCaseToSentenceCase(metadata.state ?? ""),
      deliveryStatus: snakeCaseToSentenceCase(metadata.delivery_status ?? ""),
      orderStartDate: moment.min(metadata.flights?.map((f) => moment(f.startDateTime)))?.format("MMMM DD, YYYY"),
      orderEndDate: moment.max(metadata.flights?.map((f) => moment(f.endDateTime)))?.format("MMMM DD, YYYY"),
      budget: metadata.flights?.reduce((acc, f) => acc + f.budgetAmount, 0),
      optimization: snakeCaseToSentenceCase(metadata.optimization?.bidStrategy ?? "")?.replace("Kpi", "KPI"),
      biddingPriority: metadata.optimization?.bidStrategy.automateBudgetAllocation,
    };

    return processedData;
  }, [orderOverviewData]);

  const dspLineItemTableData = useMemo(() => {
    const combinedData = lineItemMetadata?.map((metadata) => {
      const stats = lineItemData?.find((row) => row.line_item_id === metadata.ad_group_id) ?? {};
      return { ...metadata, ...stats };
    });
    return combinedData;
  }, [lineItemData, lineItemMetadata]);

  const lineItemIds = useMemo(() => {
    return lineItemMetadata?.map((row) => row.ad_group_id);
  }, [lineItemMetadata]);

  const dspCreativesData = useMemo(() => {
    const combinedData = dspCreativeStatsData?.map((stats) => {
      const metadata = dspCreativeMetadata?.find((row) => row.creative_id === stats.creative_id) ?? {};

      return { ...metadata, ...stats };
    });
    return combinedData;
  }, [dspCreativeMetadata, dspCreativeStatsData]);

  const lineItemFilterOptions = useMemo(() => {
    const options = lineItemMetadata?.map((lineItem) => {
      const { name, id, ad_group_id } = lineItem;

      return { value: id, label: name, ad_group_id };
    });

    if (!selectedLineItem) {
      setSelectedLineItem(options?.[0]);
    }

    return options;
  }, [lineItemMetadata, selectedLineItem]);
  // Overview stat tile and chart metrics
  const overviewMetrics = useMemo(() => {
    return [
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_sales", "dsp_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_total_sales", "dsp_total_sales_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_ecpm", "dsp_ecpm_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_impressions", "dsp_impressions_percent"),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_viewable_impressions",
        "dsp_viewable_impressions_percent",
      ),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_viewability", "dsp_viewability_delta"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_clicks", "dsp_clicks_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_cost", "dsp_cost_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_total_purchases", "dsp_total_purchases_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_units", "dsp_units_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_total_units", "dsp_total_units_percent"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_ctr", "dsp_ctr_delta"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_total_acos", "dsp_total_acos_delta"),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_total_roas", "dsp_total_roas_delta"),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_total_detail_page_views",
        "dsp_total_detail_page_views_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_total_detail_page_view_rate",
        "dsp_total_detail_page_view_rate_delta",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_total_add_to_cart",
        "dsp_total_add_to_cart_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_new_to_brand_detail_page_views",
        "dsp_new_to_brand_detail_page_views_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_new_to_brand_detail_page_views_rate",
        "dsp_new_to_brand_detail_page_views_rate_delta",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_total_new_to_brand_dpvs",
        "dsp_total_new_to_brand_dpvs_percent",
      ),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_total_new_to_brand_dpv_rate",
        "dsp_total_new_to_brand_dpv_rate_delta",
      ),
      buildDSPMetric(marketPlace.marketPlace, orderOverviewData, "dsp_brand_searches", "dsp_brand_searches_percent"),
      buildDSPMetric(
        marketPlace.marketPlace,
        orderOverviewData,
        "dsp_brand_searches_rate",
        "dsp_brand_searches_rate_delta",
      ),
    ];
  }, [orderOverviewData, marketPlace]);

  const breadCrumbItems = useMemo(() => {
    return [
      {
        name: "DSP Dashboard",
        href: `/user/dsp/${dspAdvertiser?.id}/dashboard`,
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: "Dashboards",
        activeOption: "DSP Dashboard",
      },
      {
        name: orderName,
        href: `/user/dsp/${dspAdvertiser?.id}/orders/${dspOrderId}`,
        dropdownLinkOptions: dspMetadata,
        searchable: true,
        searchPlaceholderText: "Search DSP orders...",
        dropdownSubtitle: "DSP Orders",
      },
    ];
  }, [dspAdvertiser, dspOrderId, dspMetadata, orderName]);

  const renderTabContent = (tab) => {
    switch (tab) {
      case "orderSettings": {
        const { optimization, creation_date_time, name, state, flights, frequencies, delivery_status } =
          dspCampaignMetadata?.[0] ?? {};

        return (
          <Col xs={12} lg={6}>
            <ReadOnlyTable borderless hover>
              <tbody>
                <tr>
                  <th className="d-flex">Name</th>
                  <td>{name}</td>
                </tr>
                <tr>
                  <th className="d-flex">State</th>
                  <td>{toTitleCase(state ?? "")}</td>
                </tr>
                <tr>
                  <th className="d-flex">Delivery Status</th>
                  <td>{toTitleCase(snakeCaseToSentenceCase(delivery_status) ?? "")}</td>
                </tr>
                <tr>
                  <th className="d-flex">Created On</th>
                  <td>{moment(creation_date_time).format("MMMM DD, YYYY")}</td>
                </tr>
                {optimization && (
                  <tr>
                    <th className="d-flex">Optimization Bidding Priority</th>
                    <td>{snakeCaseToSentenceCase(optimization?.bidStrategy)}</td>
                  </tr>
                )}
                {optimization?.automateBudgetAllocation && (
                  <tr>
                    <th className="d-flex">Optimization Budget Strategy</th>
                    <td>Automate Budget Allocation</td>
                  </tr>
                )}
                <tr>
                  <th className="d-flex">Optimization Goal</th>
                  <td>
                    {toTitleCase(optimization?.goalSetting?.goal ?? "")}
                    <br />
                    <small>{GOAL_DEFINITIONS[optimization?.goalSetting?.goal]}</small>
                  </td>
                </tr>
                <tr>
                  <th className="d-flex">Optimization KPI</th>
                  <td>
                    {snakeCaseToSentenceCase(optimization?.goalSetting?.kpi ?? "")} <br />
                    <small>{KPI_DEFINITIONS[optimization?.goalSetting?.kpi]}</small>
                  </td>
                </tr>
                <tr>
                  <th className="d-flex">Optimization KPI Target</th>
                  <td>{optimization?.goalSetting?.targetKpi}</td>
                </tr>
                <tr>
                  <th className="d-flex">Frequencies</th>
                  <td>
                    {frequencies?.map((frequency, index) => (
                      <Fragment key={index}>
                        <span>{toTitleCase(frequency.frequencyType)}</span>
                        {frequency.maxImpressions && (
                          <span>: no more than {frequency.maxImpressions} impressions a day</span>
                        )}
                      </Fragment>
                    ))}
                  </td>
                </tr>

                <tr>
                  <th className="d-flex">Flights</th>
                  <td>
                    <FlightsTable data={flights} />
                  </td>
                </tr>
              </tbody>
            </ReadOnlyTable>
          </Col>
        );
      }
      case "configuration": {
        return (
          <Col xs={12}>
            <DSPOrderForm
              initialFormData={{
                ...(dspCampaignMetadata?.[0] ?? {}),
                conversion_tracking_products: conversionTrackingProducts,
              }}
              onSuccessfulSubmit={() => {
                queryClient.invalidateQueries({
                  queryKey: ["dspCampaignData"],
                });
              }}
              campaignId={dspOrderId}
              orderEndDate={orderDates.endDate}
              dspAdvertiserId={dspAdvertiser?.advertiser_id}
            />
          </Col>
        );
      }

      case "lineItems": {
        return (
          <>
            <Col xs={12} className="d-flex justify-content-end align-items center mx-auto">
              <CenteredModal
                adPlanModal={true}
                show={showLineItemCreationModal}
                closeModal={() => {
                  setShowLineItemCreationModal(false);
                }}
                content={
                  <Row>
                    <Col xs={12}>
                      <DSPLineItemForm
                        initialFormData={{}}
                        createMode={true}
                        onCancel={() => {
                          setShowLineItemCreationModal(false);
                        }}
                        onSuccessfulSubmit={() => {
                          queryClient.invalidateQueries({
                            queryKey: ["dspLineItemData"],
                          });
                          setShowLineItemCreationModal(false);
                        }}
                        campaignId={dspOrderId}
                        advertiserId={advertiserId}
                        orderStartDate={orderDates.startDate}
                        orderEndDate={orderDates.endDate}
                      />
                    </Col>
                  </Row>
                }
              />
              <Button
                onPress={() => {
                  setShowLineItemCreationModal(true);
                }}
              >
                Create a New Line Item
              </Button>
            </Col>
            <Col xs={12}>
              <DSPLineItemsTable
                data={dspLineItemTableData}
                isLoading={isDSPCampaignDataLoading}
                campaignData={dspCampaignMetadata?.[0]}
              />
            </Col>
          </>
        );
      }
      case "creatives": {
        return (
          <Col xs={12}>
            <DSPCreativesTable
              data={dspCreativesData}
              lineItemId={selectedLineItem?.value ?? lineItemFilterOptions?.[0]?.value}
              isLoading={isDSPCreativeDataLoading}
              expanderFilters={{ order_id: dspOrderId }}
            />
          </Col>
        );
      }
      case "audienceOverlap": {
        return (
          <Col xs={12}>
            <DSPOverlapDashboard lineItemIds={lineItemIds} />
          </Col>
        );
      }
      case "geo": {
        return (
          <Col xs={12}>
            <DSPGeoDashboard orderId={dspOrderId} />
          </Col>
        );
      }

      default: {
        return null;
      }
    }
  };

  if (!hasDSPModule || channel.currentChannel !== "amazon") {
    return <Redirect to="/user/dashboard" />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row className="pb-5">
        <Col xs={12} className="px-5">
          <MetricsCard
            id="dspOverviewDashboard"
            title={orderName}
            initialSelectedMetric={["dsp_sales", "dsp_ecpm", "dsp_impressions", "dsp_total_purchases", "dsp_clicks"]}
            data={orderOverviewData}
            metrics={overviewMetrics}
            group={group}
            loading={isLoading}
            showLoadingSpinner={true}
            showMetricsSelector={true}
            context={"dspCampaign"}
            byline={
              <>
                {start.format("MMMM Do YYYY")}
                {" - "}
                {end.format("MMMM Do YYYY")}
              </>
            }
            showSkeletonLoadingEffect={true}
          >
            {/* Metadata Stat Tiles */}
            <Row className="pt-5">
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"state"}
                  name={"State"}
                  value={secondaryMetricTileData?.state}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"deliveryStatus"}
                  name={"Delivery Status"}
                  value={secondaryMetricTileData?.deliveryStatus}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"orderStartDate"}
                  name={"Start Date"}
                  value={secondaryMetricTileData?.orderStartDate}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"orderEndDate"}
                  name={"End Date"}
                  value={secondaryMetricTileData?.orderEndDate}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"orderBudget"}
                  name={"Budget"}
                  formatter={(v) => formatCurrency(v, marketPlace.marketPlace, true)}
                  value={secondaryMetricTileData?.budget}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  size={"small"}
                  id={"orderOptimization"}
                  name={"Optimization"}
                  value={secondaryMetricTileData?.optimization}
                />
              </Col>
            </Row>

            {/* Line Items Table  */}
            <Row>
              <Col xs={12} className="mt-5 ph-5 mb-5 border-bottom fs-standard">
                <Nav
                  variant="underline"
                  onSelect={(tab) => {
                    return setSelectedTab(tab);
                  }}
                  activeKey={selectedTab}
                >
                  {DSP_TABS.map((entry) => ({
                    label: entry.label,
                    key: entry.key,
                  }))

                    ?.map(({ key, label }) => {
                      return (
                        <Nav.Item key={key}>
                          <Nav.Link eventKey={key} style={{ whiteSpace: "nowrap" }}>
                            {label}
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                </Nav>
              </Col>
            </Row>
            <Row>
              {isLoading || isConversionTrackingProductsDataLoading ?
                <Col xs={12}>
                  <Loading height="300px" />
                </Col>
              : <Col xs={12}>{renderTabContent(selectedTab)}</Col>}
            </Row>
          </MetricsCard>
        </Col>
      </Row>
    </div>
  );
};

const DSPOrderDetails = () => {
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);

  return (
    <DatesProvider
      period={urlParams.get("period")}
      group={urlParams.get("group")}
      compare={urlParams.get("compare")}
      start={urlParams.get("start")}
      end={urlParams.get("end")}
    >
      <Dashboard />
    </DatesProvider>
  );
};

export default DSPOrderDetails;
